import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Folder, FolderPlus, Trash2 } from "lucide-react";
import { RecordingRow } from "@/components/app/RecordingRow";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, Empty, PageHeader } from "@/components/app/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

export default function Files() {
  const { L } = useApp();
  const { workspace, canWrite } = useAuth();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("current");
  const load = () => {
    api.get("/folders", { params: { workspace_id: workspace.id } }).then((r) => setFolders(r.data));
    api.get("/files", { params: { workspace_id: workspace.id, all_workspaces: scope === "all" } }).then((r) => setFiles(r.data));
  };
  useEffect(() => { if (workspace) load(); }, [workspace, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/folders", { workspace_id: workspace.id, name }); setName(""); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const folderName = Object.fromEntries(folders.map((f) => [f.id, f.name]));
  const recordings = Object.values(files.reduce((acc, f) => {
    const r = (acc[f.meeting_id] ||= { id: f.meeting_id, title: f.meeting_title, source: f.source, folder_id: f.folder_id, folder: f.folder_name || folderName[f.folder_id], workspaceName: f.workspace_name, workspaceType: f.workspace_type, wsId: f.meeting_workspace_id, files: [] });
    r.files.push(f);
    return acc;
  }, {}));
  const remove = async (id) => { try { await api.delete(`/folders/${id}`); load(); } catch (err) { toast.error(formatApiError(err)); } };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8" data-testid="files-page">
      <PageHeader title={L("Fail & Folder", "Files & Folders")} desc={L("Susun mesyuarat ke dalam folder dan akses fail rakaman.", "Organise meetings into folders and access recording files.")} />
      <Card className="mt-6 p-5">
        <h2 className="font-semibold">{L("Folder", "Folders")}</h2>
        {canWrite && (
          <form onSubmit={create} className="mt-3 flex max-w-md gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={L("Nama folder baharu", "New folder name")} className="h-10 rounded-xl" data-testid="new-folder-input" />
            <Button type="submit" disabled={!name.trim()} className="rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="create-folder-btn"><FolderPlus size={16} /></Button>
          </form>
        )}
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {folders.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-4" data-testid={`folder-${f.id}`}>
              <Link to={`/app/meetings?folder=${f.id}`} className="flex items-center gap-2 font-medium hover:text-violet-700"><Folder size={16} className="text-violet-600" /> {f.name} <span className="text-xs text-slate-400">({f.count})</span></Link>
              {["owner", "admin"].includes(workspace.role) && <button onClick={() => remove(f.id)} aria-label="Padam folder" data-testid={`delete-folder-${f.id}`} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>}
            </div>
          ))}
        </div>
        {folders.length === 0 && <p className="mt-3 text-sm text-slate-500">{L("Belum ada folder.", "No folders yet.")}</p>}
      </Card>
      <Card className="mt-6 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold">{L("Fail rakaman", "Recording files")} <span className="text-sm font-normal text-slate-400">({recordings.length})</span></h2>
          <div className="flex rounded-xl border border-slate-200 p-0.5 text-xs font-semibold">
            {[["current", L("Workspace ini", "This workspace")], ["all", L("Semua workspace", "All workspaces")]].map(([k, l]) => (
              <button key={k} onClick={() => setScope(k)} aria-pressed={scope === k} data-testid={`files-scope-${k}`} className={`rounded-lg px-3 py-1.5 ${scope === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="mt-3 divide-y divide-slate-100">
          {files.length === 0 && <Empty>{L("Tiada fail.", "No files.")}</Empty>}
          {recordings.map((r) => <RecordingRow key={r.id} rec={r} folders={folders} onChange={load} />)}
        </div>
      </Card>
    </div>
  );
}
